import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Icon } from "@wato/ui";
import { useApi } from "../../api/context";
import type { InboxConversation } from "../../api/types";
import { formatDate, mayUse } from "../roles/role-label";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { loadHomeSummary } from "./home-summary";

/** OWN-219 B: the latest conversations (5), each a link into the inbox. Hidden without inbox.view. */
export function HomeRecentChats({ workspaceId }: { readonly workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const allowed = mayUse(workspace, "inbox.view");
  const [items, setItems] = useState<readonly InboxConversation[] | "failed" | null>(null);
  useEffect(() => {
    if (!allowed) return;
    let live = true;
    loadHomeSummary(api, workspaceId).then(
      (s) => live && setItems(s.recent.slice(0, 5)),
      () => live && setItems("failed"),
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId, allowed]);
  if (!allowed) return null;
  const inbox = `/w/${workspaceId}/conversations`;
  return (
    <section
      aria-label={t("dashboard.home.today.recent")}
      data-testid="home-recent-chats"
      className="flex flex-col gap-2 rounded-2xl border border-app-line bg-app-surface p-6"
    >
      <div className="mb-2 flex items-center gap-2.5">
        <h3 className="m-0 flex-1 text-lg font-semibold text-app-fg">
          {t("dashboard.home.today.recent")}
        </h3>
        <Link
          to={inbox}
          aria-label={t("dashboard.home.today.allChats")}
          title={t("dashboard.home.today.allChats")}
          className="flex size-10 items-center justify-center rounded-xl text-app-muted hover:bg-app-surface-2"
        >
          <Icon name="inbox" className="size-5" />
        </Link>
      </div>
      {items === null ? (
        <p role="status" aria-busy="true" className="m-0 text-sm text-app-muted">
          {t("dashboard.shell.loading")}
        </p>
      ) : items === "failed" || items.length === 0 ? (
        <p className="m-0 text-sm text-app-muted">{t("dashboard.home.today.noChats")}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {items.map((c) => {
            const name = c.contact.displayName ?? c.contact.phoneE164 ?? "—";
            return (
              <li key={c.id}>
                <Link
                  to={inbox}
                  className="flex items-center gap-3.5 rounded-xl p-3 text-app-fg no-underline hover:bg-app-surface-2"
                >
                  <span
                    aria-hidden="true"
                    className="flex size-11 shrink-0 items-center justify-center rounded-full bg-app-surface-2 font-semibold"
                  >
                    {name.trim().charAt(0)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold">{name}</span>
                    <span className="truncate text-xs text-app-muted">
                      {c.lastMessagePreview ?? ""}
                    </span>
                  </span>
                  <span className="flex flex-col items-end gap-1.5 text-xs text-app-muted">
                    {c.lastMessageAt ? formatDate(c.lastMessageAt, locale) : null}
                    {c.unreadCount > 0 ? (
                      <span className="min-w-5 rounded-full bg-app-accent px-1.5 text-center font-bold text-app-on-accent">
                        {c.unreadCount}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
